import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronUp, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CurrencyIcon } from '@/components/profile/CurrencyIcon'
import { formatBalance } from '@/lib/format-number'
import { cn } from '@/lib/utils'
import { useGetCurrenciesQuery } from '@/store/api/paymentApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { selectCrashGame, setCrashAutoCashoutEnabled, setCrashAutoCashoutMultiplier, setCrashBetAmount, setCrashBetMode } from '@/store/slices/crashSlice'
import type { CrashBetMode } from '@/types/crash'
import type { GameController } from '@/components/original/games/crash/core/GameController'

interface CrashBetPanelProps {
	controller: GameController | null
	className?: string
}

function formatAmount(value: number) {
	return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

export function CrashBetPanel({ controller, className }: CrashBetPanelProps) {
	const dispatch = useAppDispatch()
	const crash = useAppSelector(selectCrashGame)
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	const { data: currenciesResponse } = useGetCurrenciesQuery()
	const [amountDraft, setAmountDraft] = useState(crash.betAmount)
	const [autoCashoutDraft, setAutoCashoutDraft] = useState(crash.autoCashoutMultiplier)
	const [playersOpen, setPlayersOpen] = useState(false)

	const currencies = currenciesResponse?.data ?? []

	const currency = useMemo(() => currencies.find((item) => item.currency_code.toUpperCase() === crash.currencyCode.toUpperCase()), [currencies, crash.currencyCode])

	const usdAmount = useMemo(() => {
		const amount = Number(amountDraft)
		const rate = Number(currency?.exchange_rate ?? 0)

		if (!Number.isFinite(amount) || !Number.isFinite(rate)) {
			return 0
		}

		return amount * rate
	}, [amountDraft, currency?.exchange_rate])

	useEffect(() => {
		setAmountDraft(crash.betAmount)
	}, [crash.betAmount])

	useEffect(() => {
		setAutoCashoutDraft(crash.autoCashoutMultiplier)
	}, [crash.autoCashoutMultiplier])

	const amountLocked = crash.scheduledBet || (crash.hasBet && !crash.hasCashedOut)

	const action = useMemo(() => {
		if (!isAuthenticated) {
			return {
				label: 'Bet',
				variant: 'secondary' as const,
				disabled: true,
				onClick: () => undefined,
			}
		}

		if (crash.phase === 'waiting') {
			if (crash.hasBet) {
				return { label: 'Cancel', variant: 'destructive' as const, disabled: crash.isSubmitting, onClick: () => void controller?.cancelBet() }
			}

			return {
				label: crash.autoBetEnabled ? 'Auto Bet' : 'Bet',
				variant: 'default' as const,
				disabled: crash.isSubmitting,
				onClick: () => void controller?.placeBet(),
			}
		}

		if (crash.phase === 'flying') {
			if (crash.hasCashedOut) {
				return { label: `Cashed ${formatAmount(crash.winAmount)}`, variant: 'secondary' as const, disabled: true, onClick: () => undefined }
			}

			if (crash.hasBet && crash.autoCashoutEnabled) {
				return {
					label: `Auto Cashout ${crash.autoCashoutMultiplier}x`,
					variant: 'secondary' as const,
					disabled: true,
					onClick: () => undefined,
				}
			}

			if (crash.hasBet) {
				return {
					label: `Cash Out ${crash.currentMultiplier.toFixed(2)}x`,
					variant: 'default' as const,
					disabled: crash.isSubmitting,
					onClick: () => void controller?.cashOut(),
				}
			}

			if (crash.scheduledBet) {
				return { label: 'Cancel Next', variant: 'destructive' as const, disabled: false, onClick: () => void controller?.cancelBet() }
			}

			return {
				label: crash.autoBetEnabled ? 'Auto Bet Next' : 'Bet Next',
				variant: 'secondary' as const,
				disabled: false,
				onClick: () => void controller?.placeBet(),
			}
		}

		if (crash.phase === 'crashed') {
			if (crash.scheduledBet) {
				return { label: 'Cancel Next', variant: 'destructive' as const, disabled: false, onClick: () => void controller?.cancelBet() }
			}

			return {
				label: crash.autoBetEnabled ? 'Auto Bet Next' : 'Bet Next',
				variant: 'secondary' as const,
				disabled: false,
				onClick: () => void controller?.placeBet(),
			}
		}

		if (crash.scheduledBet) {
			return { label: 'Cancel Next', variant: 'destructive' as const, disabled: false, onClick: () => void controller?.cancelBet() }
		}

		return {
			label: crash.autoBetEnabled ? 'Auto Bet' : 'Bet',
			variant: 'secondary' as const,
			disabled: true,
			onClick: () => undefined,
		}
	}, [
		controller,
		crash.autoBetEnabled,
		crash.autoCashoutEnabled,
		crash.autoCashoutMultiplier,
		crash.hasBet,
		crash.hasCashedOut,
		crash.isSubmitting,
		crash.currentMultiplier,
		crash.phase,
		crash.scheduledBet,
		crash.winAmount,
		isAuthenticated,
	])

	const handleModeChange = useCallback(
		(value: string) => {
			dispatch(setCrashBetMode(value as CrashBetMode))
		},
		[dispatch],
	)

	const commitAmount = useCallback(() => {
		const parsed = Number(amountDraft)
		if (!Number.isFinite(parsed) || parsed <= 0) {
			setAmountDraft(crash.betAmount)
			return
		}

		dispatch(setCrashBetAmount(parsed.toString()))
	}, [amountDraft, crash.betAmount, dispatch])

	const commitAutoCashout = useCallback(() => {
		let parsed = Number(autoCashoutDraft)
		if (!Number.isFinite(parsed) || parsed < 1.01) {
			parsed = 1.01
		}

		const next = parsed.toFixed(2)
		setAutoCashoutDraft(next)
		dispatch(setCrashAutoCashoutMultiplier(next))
	}, [autoCashoutDraft, dispatch])

	const handleQuickAmount = useCallback(
		(multiplier: number) => {
			const parsed = Number(amountDraft) || Number(crash.betAmount) || 0
			const next = Math.max(0, parsed * multiplier)
			const nextAmount = next > 0 ? String(next) : '0'

			setAmountDraft(nextAmount)
			dispatch(setCrashBetAmount(nextAmount))
		},
		[amountDraft, crash.betAmount, dispatch],
	)

	return (
		<div className={cn('flex h-full min-h-0 w-full flex-col gap-4 overflow-hidden rounded-2xl border border-border bg-card p-4', className)}>
			<Tabs value={crash.betMode} onValueChange={handleModeChange} className='shrink-0'>
				<TabsList className='grid h-11 w-full grid-cols-2 rounded-xl bg-secondary p-1'>
					<TabsTrigger value='manual' className='rounded-lg data-[state=active]:bg-background'>
						Manual
					</TabsTrigger>
					<TabsTrigger value='auto' className='rounded-lg data-[state=active]:bg-background'>
						Auto
					</TabsTrigger>
				</TabsList>
			</Tabs>

			<div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto'>
			<div className={cn('space-y-2', amountLocked && 'pointer-events-none opacity-50')}>
				<div className='flex items-center justify-between gap-3'>
					<label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>Bet Amount</label>
					<span className='text-xs font-medium text-muted-foreground'>${formatBalance(usdAmount, 2)}</span>
				</div>
				<div className='flex items-stretch overflow-hidden rounded-xl border border-border bg-secondary'>
					<div className='relative min-w-0 flex-1'>
						<input
							type='text'
							inputMode='decimal'
							value={amountDraft}
							onChange={(event) => setAmountDraft(event.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1'))}
							onBlur={commitAmount}
							className='flex h-11 w-full bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none sm:text-base sm:tracking-[-0.24px]'
						/>
						<CurrencyIcon
							code={crash.currencyCode}
							symbolUrl={currency?.symbol_url}
							size='sm'
							className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2'
						/>
					</div>
					<div className='flex w-[88px] shrink-0 border-l border-border'>
						<button type='button' onClick={() => handleQuickAmount(0.5)} className='flex-1 text-sm font-medium text-foreground transition-colors hover:bg-accent'>
							½
						</button>
						<span className='w-px bg-border' aria-hidden />
						<button type='button' onClick={() => handleQuickAmount(2)} className='flex-1 text-sm font-medium text-foreground transition-colors hover:bg-accent'>
							2×
						</button>
					</div>
				</div>
			</div>

			<div className='space-y-2'>
				<div className='flex items-center justify-between gap-3'>
					<label htmlFor='crash-auto-cashout' className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
						Auto Cash Out
					</label>
					<Switch
						id='crash-auto-cashout'
						checked={crash.autoCashoutEnabled}
						onCheckedChange={(checked) => dispatch(setCrashAutoCashoutEnabled(checked))}
						disabled={amountLocked}
						aria-label='Auto cash out'
					/>
				</div>
				<input
					type='text'
					inputMode='decimal'
					value={autoCashoutDraft}
					disabled={!crash.autoCashoutEnabled || amountLocked}
					onChange={(event) => setAutoCashoutDraft(event.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1'))}
					onBlur={commitAutoCashout}
					className='flex h-11 w-full rounded-xl border border-border bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none sm:text-base sm:tracking-[-0.24px]'
				/>
			</div>

			<Collapsible
				open={playersOpen}
				onOpenChange={setPlayersOpen}
				className={cn(
					'flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-secondary/60',
					playersOpen ? 'max-h-[240px] flex-1 xl:max-h-none' : 'shrink-0',
				)}
			>
				<CollapsibleTrigger className='flex h-11 w-full shrink-0 items-center gap-2.5 px-3 text-sm transition-colors hover:bg-background/30'>
					<Users className='h-6 w-6 shrink-0 text-muted-foreground' aria-hidden />
					<span className='font-medium tabular-nums text-foreground'>{crash.playerCount}</span>
					<span className='ml-auto flex min-w-0 items-center gap-1.5'>
						<CurrencyIcon code={crash.currencyCode} symbolUrl={currency?.symbol_url} size='sm' className='shrink-0' />
						<span className='truncate font-medium tabular-nums text-foreground'>{formatBalance(crash.totalBetAmount)}</span>
					</span>
					<ChevronUp className={cn('h-6 w-6 shrink-0 text-muted-foreground transition-transform duration-200', !playersOpen && 'rotate-180')} aria-hidden />
				</CollapsibleTrigger>

				<CollapsibleContent className='min-h-0 flex-1 overflow-hidden data-[state=closed]:hidden'>
					<div className='h-full max-h-[200px] overflow-y-auto border-t border-border xl:max-h-none'>
						{crash.players.length === 0 ? (
							<p className='px-3 py-5 text-center text-sm text-muted-foreground'>Waiting for players…</p>
						) : (
							<ul>
								{crash.players.map((player) => {
									const displayMultiplier = player.cashoutMultiplier ?? (crash.phase === 'flying' ? crash.currentMultiplier : null)
									const playerCurrency = currencies.find((item) => item.currency_code.toUpperCase() === player.currencyCode.toUpperCase())
									const displayName = player.username?.trim() || 'Hidden'

									return (
										<li
											key={player.playerId}
											className='grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 px-3 py-1.5 text-sm hover:bg-background/40'
										>
											<span className={cn('truncate', displayName === 'Hidden' ? 'text-muted-foreground' : 'font-medium text-foreground')}>
												{displayName}
											</span>
											<span
												className={cn(
													'min-w-[3.25rem] text-right tabular-nums',
													player.cashoutMultiplier != null ? 'font-semibold text-success' : 'text-foreground',
												)}
											>
												{displayMultiplier != null ? `${displayMultiplier.toFixed(2)}x` : '—'}
											</span>
											<span className='flex min-w-[4.5rem] items-center justify-end gap-1.5 tabular-nums text-foreground'>
												<CurrencyIcon code={player.currencyCode} symbolUrl={playerCurrency?.symbol_url} size='sm' />
												<span>{formatAmount(player.betAmount)}</span>
											</span>
										</li>
									)
								})}
							</ul>
						)}
					</div>
				</CollapsibleContent>
			</Collapsible>
			</div>

			<div className='shrink-0'>
			<Button type='button' variant={action.variant} disabled={action.disabled || !controller} onClick={action.onClick} className='h-12 w-full text-base font-semibold'>
				{action.label}
			</Button>
			</div>
		</div>
	)
}
